import React, { useEffect, useState } from 'react';
import { Button, Card, Form, Message } from 'semantic-ui-react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { API, showError, showSuccess } from '../../helpers';

const EditPlan = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const isEdit = !!id;
  const navigate = useNavigate();

  const [form, setForm] = useState({
    code: '',
    name: '',
    level: 'standard',
    price_cents: 0,
    period_days: 30,
    monthly_quota: 0,
    request_limit_5h: 0,
    request_limit_week: 0,
    concurrency_limit: 0,
    quota_exhaust_strategy: 'hard_cutoff',
    description: '',
    status: 1,
    models: '',
  });
  const [modelOptions, setModelOptions] = useState([]);
  const [selectedModels, setSelectedModels] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // 加载所有可用模型（复用 channel/models 接口）
  useEffect(() => {
    const loadModels = async () => {
      try {
        const res = await API.get('/api/channel/enabled_models');
        const { success, data } = res.data;
        if (success && Array.isArray(data)) {
          setModelOptions(data.map((m) => ({ key: m, text: m, value: m })));
        }
      } catch (e) {
        // 模型列表加载失败不阻塞表单
      }
    };
    loadModels();
  }, []);

  // 编辑模式：加载套餐
  useEffect(() => {
    if (!isEdit) return;
    const loadPlan = async () => {
      try {
        const res = await API.get('/api/subscription/plan/all');
        const { success, data, message } = res.data;
        if (!success) {
          showError(message);
          return;
        }
        const plan = (data || []).find((p) => p.id === parseInt(id));
        if (!plan) {
          setError(t('subscription_plan.not_found'));
          return;
        }
        setForm({
          code: plan.code || '',
          name: plan.name || '',
          level: plan.level || 'standard',
          price_cents: plan.price_cents || 0,
          period_days: plan.period_days || 30,
          monthly_quota: plan.monthly_quota || 0,
          request_limit_5h: plan.request_limit_5h || 0,
          request_limit_week: plan.request_limit_week || 0,
          concurrency_limit: plan.concurrency_limit || 0,
          quota_exhaust_strategy: plan.quota_exhaust_strategy || 'hard_cutoff',
          description: plan.description || '',
          status: plan.status || 1,
          models: plan.models || '',
        });
        if (plan.models) {
          try {
            setSelectedModels(JSON.parse(plan.models));
          } catch {
            setSelectedModels([]);
          }
        }
      } catch (e) {
        showError(e.message);
      }
    };
    loadPlan();
  }, [id, isEdit, t]);

  const handleChange = (e, { name, value }) => {
    setForm((f) => ({ ...f, [name]: value }));
  };

  const handleModelsChange = (e, { value }) => {
    setSelectedModels(value);
    setForm((f) => ({ ...f, models: JSON.stringify(value) }));
  };

  const submit = async () => {
    setLoading(true);
    setError('');
    try {
      const payload = { ...form };
      // 数值字段强转
      ['price_cents', 'period_days', 'monthly_quota', 'request_limit_5h', 'request_limit_week', 'concurrency_limit'].forEach((k) => {
        payload[k] = parseInt(payload[k]) || 0;
      });
      payload.status = parseInt(payload.status) || 1;

      let res;
      if (isEdit) {
        res = await API.put(`/api/subscription/plan/${id}`, payload);
      } else {
        res = await API.post('/api/subscription/plan/', payload);
      }
      const { success, message } = res.data;
      if (success) {
        showSuccess(isEdit ? t('subscription_plan.updated') : t('subscription_plan.created'));
        navigate('/subscription-plan');
      } else {
        setError(message);
      }
    } catch (e) {
      setError(e.message);
    }
    setLoading(false);
  };

  return (
    <div className='dashboard-container'>
      <Card fluid className='chart-card'>
        <Card.Content>
          <Card.Header className='header'>
            {isEdit ? t('subscription_plan.edit') : t('subscription_plan.add')}
          </Card.Header>
          {error && <Message negative>{error}</Message>}
          <Form onSubmit={submit} loading={loading}>
            <Form.Group widths='equal'>
              <Form.Input
                label={t('subscription_plan.code')}
                name='code'
                value={form.code}
                onChange={handleChange}
                required
                disabled={isEdit}
              />
              <Form.Input
                label={t('subscription_plan.name')}
                name='name'
                value={form.name}
                onChange={handleChange}
                required
              />
              <Form.Dropdown
                label={t('subscription_plan.level')}
                name='level'
                selection
                options={[
                  { key: 'free', text: 'Free', value: 'free' },
                  { key: 'standard', text: 'Standard', value: 'standard' },
                  { key: 'pro', text: 'Pro', value: 'pro' },
                  { key: 'max', text: 'Max', value: 'max' },
                  { key: 'ultra', text: 'Ultra', value: 'ultra' },
                ]}
                value={form.level}
                onChange={handleChange}
              />
            </Form.Group>
            <Form.Group widths='equal'>
              <Form.Input
                label={t('subscription_plan.price_cents')}
                name='price_cents'
                type='number'
                value={form.price_cents}
                onChange={handleChange}
              />
              <Form.Input
                label={t('subscription_plan.period_days')}
                name='period_days'
                type='number'
                value={form.period_days}
                onChange={handleChange}
              />
              <Form.Input
                label={t('subscription_plan.monthly_quota')}
                name='monthly_quota'
                type='number'
                value={form.monthly_quota}
                onChange={handleChange}
              />
            </Form.Group>
            <Form.Group widths='equal'>
              <Form.Input
                label={t('subscription_plan.request_limit_5h')}
                name='request_limit_5h'
                type='number'
                value={form.request_limit_5h}
                onChange={handleChange}
              />
              <Form.Input
                label={t('subscription_plan.request_limit_week')}
                name='request_limit_week'
                type='number'
                value={form.request_limit_week}
                onChange={handleChange}
              />
              <Form.Input
                label={t('subscription_plan.concurrency_limit')}
                name='concurrency_limit'
                type='number'
                value={form.concurrency_limit}
                onChange={handleChange}
              />
            </Form.Group>
            <Form.Group widths='equal'>
              <Form.Dropdown
                label={t('subscription_plan.quota_exhaust_strategy')}
                name='quota_exhaust_strategy'
                selection
                options={[
                  { key: 'hard_cutoff', text: t('subscription_plan.hard_cutoff'), value: 'hard_cutoff' },
                  { key: 'overage_billing', text: t('subscription_plan.overage_billing'), value: 'overage_billing' },
                ]}
                value={form.quota_exhaust_strategy}
                onChange={handleChange}
              />
              <Form.Dropdown
                label={t('subscription_plan.status')}
                name='status'
                selection
                options={[
                  { key: 1, text: t('subscription_plan.online'), value: 1 },
                  { key: 2, text: t('subscription_plan.offline'), value: 2 },
                ]}
                value={form.status}
                onChange={handleChange}
              />
            </Form.Group>
            <Form.Dropdown
              label={t('subscription_plan.models_label')}
              placeholder={t('subscription_plan.models_placeholder')}
              name='models'
              fluid
              multiple
              search
              selection
              options={modelOptions}
              value={selectedModels}
              onChange={handleModelsChange}
            />
            <Form.TextArea
              label={t('subscription_plan.description')}
              name='description'
              value={form.description}
              onChange={handleChange}
            />
            <Button primary type='submit' loading={loading}>
              {t('subscription_plan.save')}
            </Button>
            <Button type='button' onClick={() => navigate('/subscription-plan')}>
              {t('subscription_plan.cancel')}
            </Button>
          </Form>
        </Card.Content>
      </Card>
    </div>
  );
};

export default EditPlan;
